/**
 * CreditValue: one figure in the current reading. Actual shows the actual,
 * Weighted the weighted, Both "actual / weighted" with the weighted half muted.
 * `weightable: false` (brokers, ratings) always shows the actual. Zero shows a
 * dot so a dense table stays quiet.
 */

import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { COLORS } from '@shared/ui';
import { formatCredit, type CreditMode } from '@shared/lib/sales-credit';
import { useCreditMode } from './CreditModeProvider';

const useStyles = makeStyles({
  muted: { color: COLORS.textMuted, fontWeight: 400 },
  zero: { color: '#c4a98a' },
});

export interface CreditReading {
  mode: CreditMode;
  /** Weighted figures are in play (Weighted or Both). */
  showWeighted: boolean;
  /** Both figures are shown side by side. */
  showBoth: boolean;
  /** The single number a sort or a chart should use in this reading. */
  pick: (actual: number, weighted: number) => number;
  /** "EE Lives" to "EE Lives (wtd)" or "EE Lives (act / wtd)". */
  suffix: string;
}

export function useCreditReading(): CreditReading {
  const { mode } = useCreditMode();
  const showWeighted = mode !== 'actual';
  const showBoth = mode === 'both';
  return {
    mode,
    showWeighted,
    showBoth,
    pick: (actual, weighted) => (mode === 'weighted' ? weighted : actual),
    suffix: mode === 'weighted' ? ' (wtd)' : mode === 'both' ? ' (act / wtd)' : '',
  };
}

interface CreditValueProps {
  actual: number;
  weighted: number;
  /** False for a figure weights never touch; the actual shows in every mode. */
  weightable?: boolean;
  className?: string;
  /** Rendered for a zero in the reading; default a middle dot. */
  zero?: string;
}

export function CreditValue({ actual, weighted, weightable = true, className, zero = '·' }: CreditValueProps) {
  const styles = useStyles();
  const { mode } = useCreditMode();
  const showBoth = weightable && mode === 'both';
  const main = weightable && mode === 'weighted' ? weighted : actual;
  const isZero = showBoth ? actual === 0 && weighted === 0 : main === 0;
  if (isZero) return <span className={mergeClasses(styles.zero, className)}>{zero}</span>;
  return (
    <span className={className}>
      {formatCredit(main)}
      {showBoth && <span className={styles.muted}> / {formatCredit(weighted)}</span>}
    </span>
  );
}
